'use client';

import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import {
  FILING_STEPS,
  FILING_STEPS_WITH_ADDENDUM,
  getFilingStep,
  getTranscriptStep,
  TRANSCRIPT_STEPS,
} from 'src/components/advisor-dashboard/taxes/tax-utils';

import type { TaxReturnStatus, TaxTranscriptRequestStatus } from '@savvy/constants/generated/gql-enums';

type TaxStatusBarProps = {
  readonly title?: string;
  readonly flowType: 'transcript' | 'filing';
  readonly taxTranscriptStatus: TaxTranscriptRequestStatus | null;
  readonly federalReturnStatus?: TaxReturnStatus | null;
  readonly stateReturnStatus?: TaxReturnStatus | null;
  readonly hideTitle?: boolean;
  readonly taxAddendumPending: boolean;
  readonly taxAddendumRequired: boolean;
  readonly className?: string;
  readonly completedBarClassName?: string;
};

export function TaxStatusBar({
  title = 'Tax filing status',
  flowType,
  taxTranscriptStatus,
  federalReturnStatus,
  stateReturnStatus,
  hideTitle = false,
  taxAddendumPending,
  taxAddendumRequired,
  className,
  completedBarClassName = 'bg-green-400',
}: TaxStatusBarProps) {
  const steps = getSteps(flowType, taxAddendumRequired);

  const currentStepIndex: number =
    flowType === 'transcript'
      ? getTranscriptStep(taxTranscriptStatus)
      : getFilingStep(
          federalReturnStatus ?? null,
          stateReturnStatus ?? null,
          taxTranscriptStatus !== null,
          taxAddendumPending,
          taxAddendumRequired,
        );

  const statusBars = steps.map((step, index) => {
    //First step has no bar
    if (index === 0) return null;

    const key = `${step}-${index}`;
    const isComplete = index <= currentStepIndex;
    return (
      <div key={key} className={cn('h-1 w-full rounded-sm', isComplete ? completedBarClassName : 'bg-border-light')} />
    );
  });

  const progressProportion = currentStepIndex / (steps.length - 1);
  const numberBadgePosition = `calc(${progressProportion * 100}% + ${progressProportion * 3}px)`;

  return (
    <div className={cn('flex w-[237px] flex-col gap-1', className)}>
      {!hideTitle && (
        <Text variant="subtitle-lg" color="secondary">
          {title}
        </Text>
      )}
      <div
        role="progressbar"
        aria-label={title}
        aria-valuemin={1}
        aria-valuemax={steps.length}
        aria-valuenow={currentStepIndex + 1}
        aria-valuetext={`Step ${currentStepIndex + 1} of ${steps.length}: ${steps[currentStepIndex]}`}
        className="relative flex h-5 w-full items-center gap-1"
      >
        <div
          className="absolute top-1/2 z-1 flex size-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card"
          style={{ left: numberBadgePosition }}
        >
          <Text variant="caption" color="dark" className="font-semibold">
            {currentStepIndex + 1}
          </Text>
        </div>
        {statusBars}
      </div>
      <Text>{steps[currentStepIndex]}</Text>
    </div>
  );
}

function getSteps(flowType: 'transcript' | 'filing', taxAddendumRequired: boolean) {
  if (flowType === 'transcript') {
    return TRANSCRIPT_STEPS;
  }
  if (taxAddendumRequired) {
    return FILING_STEPS_WITH_ADDENDUM;
  }
  return FILING_STEPS;
}
